import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Globe, Menu } from "lucide-react";
import { LANGUAGES, useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/shell/Logo";
import { post } from "../lib/api";
import { saveRef, visitorId } from "../lib/referral";
import { useKeyboardOpen } from "../lib/viewport";
import en from "../i18n/public/en.json";
import Landing from "./Landing";
import { Features, Pricing } from "./InfoPages";
import { Article, HelpIndex } from "./Help";
import { Legal } from "./Legal";
import { BlogArticle, BlogIndex } from "./Blog";
import { AcademyIndex, CoursePage, LessonPage } from "./Academy";

const MobileMenu = lazy(() => import("./MobileMenu"));

const CODES = LANGUAGES.map(([c]) => c);
const PubCtx = createContext(null);
const get = (o, key) => key.split(".").reduce((x, k) => (x && x[k] !== undefined ? x[k] : undefined), o);

export const usePub = () => useContext(PubCtx);

// Public copy for the URL language starts downloading with the app bundle, not after the first render.
const PUB = {};
const loadPublic = (lang) => import(`../i18n/public/${lang}.json`).then((m) => { PUB[lang] = m.default; return m.default; });
const bootLang = window.location.pathname.split("/")[1];
export const publicReady = CODES.includes(bootLang) && bootLang !== "en" ? loadPublic(bootLang).catch(() => {}) : Promise.resolve();

const usePublicContent = (lang) => {
  const [c, setC] = useState(lang === "en" ? en : (PUB[lang] || null));
  useEffect(() => {
    if (lang === "en") { setC(en); return; }
    loadPublic(lang).then(setC).catch(() => setC(en));
  }, [lang]);
  return c;
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export const useHead = (title, desc, { langs = CODES, image = null, type = "website" } = {}) => {
  const { lang, path } = usePub();
  const langKey = langs.join(",");
  useEffect(() => {
    const origin = window.location.origin.replace("://www.", "://");
    const codes = langKey.split(",");
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:locale", lang);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", `${origin}/${lang}${path}`);
    setMeta("property", "og:site_name", "Auditmail");
    setMeta("property", "og:image", image ? `${process.env.REACT_APP_BACKEND_URL}${image}` : `${origin}/og-default.jpg`);
    setMeta("name", "twitter:card", "summary_large_image");
    document.head.querySelectorAll("link[data-am-seo]").forEach((l) => l.remove());
    const add = (rel, href, hreflang) => {
      const l = document.createElement("link");
      l.rel = rel; l.href = href; l.dataset.amSeo = "1";
      if (hreflang) l.hreflang = hreflang;
      document.head.appendChild(l);
    };
    add("canonical", `${origin}/${lang}${path}`);
    codes.forEach((c) => add("alternate", `${origin}/${c}${path}`, c));
    add("alternate", `${origin}/${codes.includes("en") ? "en" : codes[0]}${path}`, "x-default");
  }, [title, desc, lang, path, langKey, image, type]);
};

const LangSwitch = () => {
  const { lang, path } = usePub();
  const navigate = useNavigate();
  return (
    <label className="pub-lang" data-testid="public-language-switcher">
      <Globe className="h-4 w-4" />
      <select value={lang} onChange={(e) => navigate(`/${e.target.value}${path}`)} aria-label="Language" data-testid="public-language-select">
        {LANGUAGES.map(([c, name]) => <option key={c} value={c}>{name}</option>)}
      </select>
    </label>
  );
};

const Header = () => {
  const { pt, lang } = usePub();
  const { me } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const item = (to, key, label) => <NavLink to={`/${lang}${to}`} onClick={() => setOpen(false)} className={({ isActive }) => `pub-nav-item ${isActive ? "is-active" : ""}`} data-testid={`public-nav-${key}${open ? "-mobile" : ""}`}>{label || pt(`nav.${key}`)}</NavLink>;
  const links = <>{item("/features", "features")}{item("/pricing", "pricing")}{item("/blog", "blog", t("blog.nav"))}{item("/academy", "academy", t("academy.nav"))}{item("/help", "help")}</>;
  return (
    <header className="pub-header">
      <div className="pub-wrap pub-header-row">
        <button type="button" className="pub-burger" onClick={() => setOpen(true)} aria-label={t("nav.menu")} data-testid="public-mobile-menu-button"><Menu className="h-5 w-5" /></button>
        <Link to={`/${lang}/`} className="pub-brand" aria-label="Auditmail" data-testid="public-brand"><Logo size={26} /><span>Auditmail</span></Link>
        <nav className="pub-nav">{!open && links}</nav>
        {open && <Suspense fallback={null}><MobileMenu open={open} setOpen={setOpen} lang={lang} links={links} me={me} pt={pt} /></Suspense>}
        <div className="pub-actions">
          <LangSwitch />
          {me ? <Link to="/dashboard" className="pub-btn pub-btn-dark" data-testid="public-open-app">Auditmail →</Link> : (<>
            <Link to="/login" className="pub-link hidden sm:inline" data-testid="public-login-link">{pt("nav.login")}</Link>
            <Link to="/signup" className="pub-btn pub-btn-dark" data-testid="public-signup-link">{pt("nav.start")}</Link></>)}
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  const { pt, lang } = usePub();
  const { t } = useI18n();
  const l = (to, label, id) => <Link to={`/${lang}${to}`} data-testid={`footer-${id}`}>{label}</Link>;
  return (
    <footer className="pub-footer">
      <div className="pub-wrap pub-footer-grid">
        <div><p className="pub-brand"><Logo size={22} /><span>Auditmail</span></p><p className="pub-muted">{pt("footer.tagline")}</p></div>
        <div><p className="pub-foot-h">{pt("footer.product")}</p>{l("/features", pt("nav.features"), "features")}{l("/pricing", pt("nav.pricing"), "pricing")}</div>
        <div><p className="pub-foot-h">{pt("footer.resources")}</p>{l("/blog", t("blog.nav"), "blog")}{l("/academy", t("academy.nav"), "academy")}{l("/help", pt("nav.help"), "help")}<Link to="/signup">{pt("nav.start")}</Link></div>
        <div><p className="pub-foot-h">{pt("footer.legal")}</p>{l("/terms", pt("footer.terms"), "terms")}{l("/privacy", pt("footer.privacy"), "privacy")}{l("/cookies", pt("footer.cookies"), "cookies")}</div>
      </div>
      <p className="pub-wrap pub-muted pub-copy">{pt("footer.rights", { year: new Date().getFullYear() })}</p>
    </footer>
  );
};

const CookieBanner = () => {
  const { pt, lang } = usePub();
  const [choice, setChoice] = useState(() => localStorage.getItem("am_cookie_consent"));
  useEffect(() => {
    const on = () => setChoice(null);
    window.addEventListener("am:cookie-reopen", on);
    return () => window.removeEventListener("am:cookie-reopen", on);
  }, []);
  if (choice) return null;
  const pick = (v) => { localStorage.setItem("am_cookie_consent", v); setChoice(v); window.dispatchEvent(new Event("am:consent")); };
  return (
    <div className="pub-cookie" role="dialog" aria-live="polite" aria-label={pt("cookie.accept")} data-testid="cookie-banner">
      <p>{pt("cookie.text")} <Link to={`/${lang}/cookies`} className="underline" data-testid="cookie-policy-link">{pt("cookie.more")}</Link></p>
      <div className="flex gap-2">
        <button className="pub-btn pub-btn-ghost" onClick={() => pick("necessary")} data-testid="cookie-reject-button">{pt("cookie.reject")}</button>
        <button className="pub-btn pub-btn-dark" onClick={() => pick("all")} data-testid="cookie-accept-button">{pt("cookie.accept")}</button>
      </div>
    </div>
  );
};

// Compact "Start free" bar: only after the hero has scrolled away, hidden near the footer, with the keyboard open or while the cookie banner shows.
const StickyCta = () => {
  const { pt } = usePub();
  const { me } = useAuth();
  const location = useLocation();
  const keyboard = useKeyboardOpen();
  const [past, setPast] = useState(false);
  const [nearFoot, setNearFoot] = useState(false);
  const [consent, setConsent] = useState(() => !!localStorage.getItem("am_cookie_consent"));
  useEffect(() => {
    const on = () => setConsent(true), off = () => setConsent(false);
    window.addEventListener("am:consent", on);
    window.addEventListener("am:cookie-reopen", off);
    return () => { window.removeEventListener("am:consent", on); window.removeEventListener("am:cookie-reopen", off); };
  }, []);
  useEffect(() => {
    setPast(false); setNearFoot(false);
    const hero = document.querySelector("[data-sticky-anchor]");
    const foot = document.querySelector(".pub-footer, .pub-final");
    if (!hero) return undefined;
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.target === hero) setPast(!e.isIntersecting && e.boundingClientRect.top < 0);
      else setNearFoot(e.isIntersecting);
    }));
    io.observe(hero);
    document.querySelectorAll(".pub-footer, .pub-final").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);
  const show = !me && consent && past && !nearFoot && !keyboard;
  return (
    <div className={`pub-sticky-cta ${show ? "is-on" : ""}`} aria-hidden={!show} data-testid="sticky-cta-bar">
      <span className="truncate">{pt("home.note")}</span>
      <Link to="/signup" tabIndex={show ? 0 : -1} className="pub-btn pub-btn-accent" data-testid="sticky-cta-signup">{pt("nav.start")}<ArrowRight className="am-flip h-4 w-4" /></Link>
    </div>
  );
};

export const PublicSite = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { lang: uiLang, setLang } = useI18n();
  const { me } = useAuth();
  const profileLang = useRef(null);
  profileLang.current = me?.user?.language || localStorage.getItem("am_lang");
  const c = usePublicContent(CODES.includes(lang) ? lang : "en");
  useEffect(() => { if (CODES.includes(lang) && uiLang !== lang) setLang(lang, false); }, [lang, uiLang, setLang]);
  useEffect(() => () => { if (profileLang.current) setLang(profileLang.current, false); }, [setLang]);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (!CODES.includes(lang)) return <Navigate to="/dashboard" replace />;
  if (!c) return <div className="pub-root" />;
  const path = location.pathname.slice(lang.length + 1) || "/";
  const pt = (key, vars = {}) => {
    const raw = get(c, key) ?? get(en, key) ?? key;
    return typeof raw === "string" ? raw.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? "") : raw;
  };
  return (
    <PubCtx.Provider value={{ lang, path, pt, content: c, en }}>
      <div className="pub-root" lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} data-testid="public-site">
        <Header />
        <main>
          <Routes>
            <Route index element={<Landing />} />
            <Route path="features" element={<Features />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="help" element={<HelpIndex />} />
            <Route path="help/:slug" element={<Article />} />
            <Route path="blog" element={<BlogIndex />} />
            <Route path="blog/:slug" element={<BlogArticle />} />
            <Route path="academy" element={<AcademyIndex />} />
            <Route path="academy/:course" element={<CoursePage />} />
            <Route path="academy/:course/:lesson" element={<LessonPage />} />
            <Route path=":doc" element={<Legal />} />
          </Routes>
        </main>
        <Footer />
        {(path === "/" || path === "/pricing") && <StickyCta />}
        <CookieBanner />
      </div>
    </PubCtx.Provider>
  );
};

export const RootRedirect = () => {
  const { me } = useAuth();
  const { lang } = useI18n();
  if (me === null) return null;
  return <Navigate to={me ? "/dashboard" : `/${lang}/`} replace />;
};

export const ReferralLanding = () => {
  const { code } = useParams();
  const { lang } = useI18n();
  const [done, setDone] = useState(false);
  useEffect(() => {
    post("/affiliates/click", { code, visitor_id: visitorId() }).then((r) => { if (r.valid) saveRef(r.code); }).finally(() => setDone(true));
  }, [code]);
  return done ? <Navigate to={`/${lang}/`} replace /> : <div className="am-splash" data-testid="referral-landing">Auditmail</div>;
};
